import React, { useRef, useEffect, useState, useContext } from "react";
import ct from "class-types.macro";
import { Modal, ModalEvents } from "@clarkinc/zest-design-system";
import { Tooltip } from "@clarkinc/zest-design-system/ui";
import { SectionGroupItem } from "./Filters";
import { shouldNoFollow } from "./FilterUrlHelpers";
import FastAnchor from "~/common/FastAnchor";
import Icon from "~/common/Icons/Standard";
import { FilterModalContext } from "../FilterModalContextProvider";

interface Props {
  groupItem: SectionGroupItem;
  groupsWithMultipleSelections: number;
  selectedItemsInGroup: number;
  prefetch?: boolean;
}

const ColorSwatch = ({ groupItem, prefetch }: Props) => {
  const { isSelected, label, nameEncoded } = groupItem;

  const [selected, setIsSelected] = useState(isSelected);

  const wasSelected = useRef<boolean>(false);

  const noFollow = shouldNoFollow(groupItem.filterUrl) ? "nofollow" : undefined;

  const { compareProductsActive, storedFilterUrl } =
    useContext(FilterModalContext);

  const shouldOpenModal = compareProductsActive && storedFilterUrl;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsSelected(!isSelected);
    if (shouldOpenModal) {
      Modal.open("confirm-edit-filters-warning");

      storedFilterUrl.current.url = groupItem.filterUrl;
      wasSelected.current = true;
    } else {
      window.location.assign(groupItem.filterUrl);
    }
  };

  useEffect(
    function initFilterNavModalSub() {
      return Modal.subscribe(
        "confirm-edit-filters-warning",
        ModalEvents.CLOSE,
        () => {
          if (wasSelected.current && !storedFilterUrl?.current.confirmed) {
            setIsSelected((prev) => !prev);
            wasSelected.current = false;
          }
        },
      );
    },
    [groupItem.filterUrl, storedFilterUrl],
  );

  return (
    <Tooltip content={label} key={nameEncoded}>
      <li
        className="list-none h-7 mb-3 mx-1 mt-1-1/2"
        style={{ width: "29.5px" }}
        data-testid="color-swatch"
      >
        <FastAnchor
          data-testid={`color-filter-checkbox-${groupItem.label}`}
          className={`${selected ? " selected" : ""} ${ct(
            "hover:bg-white",
            "border-t-0",
            "m-0",
            "mx-0",
            "mb-1",
            "p-0",
            "relative",
            "flex-0-1-50",
            "xs:block",
            "xs:mb-0",
            "xs:text-center",
            "xs:flex-0-1-20",
            "xs:text-indent-0",
          )}`}
          style={{ width: selected ? "90%" : "100%" }}
          href={groupItem.filterUrl}
          rel={noFollow || undefined}
          onClick={handleClick}
          prefetch={Boolean(prefetch)}
          wait={200}
        >
          <span
            className={`${ct(
              "bg-green-500",
              "rounded-full",
              "p-1/2",
              "absolute",
              "-right-2.5",
              "-top-1",
            )}  ${selected ? ct("flex") : ct("hidden")}`}
          >
            <Icon
              name="cancel"
              className={ct("h-2", "fill-current", "text-white", "w-2")}
            />
          </span>
          <label>
            <input
              type="checkbox"
              autoComplete="off"
              checked={selected}
              className={ct("hidden")}
              onChange={() => setIsSelected(!selected)}
            />
            <img
              title={label}
              src={`//cdnimg.webstaurantstore.com/uploads/seo_category_toppers/2015/5/color${nameEncoded}.jpg`}
              alt={label}
              data-no-jquery
              className={`tooltip-notice ${ct(
                "border-gray-200",
                "border-solid",
                "border",
                "transition-all",
                "ease-linear",
                "duration-100",
                "cursor-pointer",
              )} ${
                selected
                  ? ct(
                      "border-green-500",
                      "border",
                      "p-1/2",
                      "transition-all",
                      "ease-linear",
                      "duration-100",
                    )
                  : ""
              }`}
            />
          </label>
        </FastAnchor>
      </li>
    </Tooltip>
  );
};

export default ColorSwatch;
